import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Paper,
  Container,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../../utils/api';
import { useLanguage } from '../../utils/LanguageContext';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();

  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const content = {
    en: {
      title: 'Reset Password',
      subtitle: 'Enter your new password below',
      passwordLabel: 'New Password',
      confirmLabel: 'Confirm Password',
      resetBtn: 'Reset Password',
      successTitle: 'Password Reset!',
      successMessage: 'Your password has been successfully reset.',
      signInBtn: 'Sign In Now',
      passwordMin: 'Password must be at least 6 characters',
      passwordMatch: 'Passwords do not match',
      invalidToken: 'Invalid or expired reset link',
      noToken: 'No reset token found. Please request a new password reset.',
      requestNew: 'Request New Link',
    },
    my: {
      title: 'Tetapkan Semula Kata Laluan',
      subtitle: 'Masukkan kata laluan baru anda',
      passwordLabel: 'Kata Laluan Baru',
      confirmLabel: 'Sahkan Kata Laluan',
      resetBtn: 'Tetapkan Semula',
      successTitle: 'Kata Laluan Ditetapkan!',
      successMessage: 'Kata laluan anda telah berjaya ditetapkan semula.',
      signInBtn: 'Log Masuk Sekarang',
      passwordMin: 'Kata laluan mesti sekurang-kurangnya 6 aksara',
      passwordMatch: 'Kata laluan tidak sepadan',
      invalidToken: 'Pautan tetapan semula tidak sah atau telah tamat',
      noToken: 'Tiada token tetapan semula. Sila minta tetapan semula kata laluan baru.',
      requestNew: 'Minta Pautan Baru',
    }
  };

  const t = content[language] || content.en;

  // No token provided
  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
          </Box>

          <Typography variant="h5" fontWeight={600} gutterBottom>
            {t.invalidToken}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t.noToken}
          </Typography>

          <Button
            variant="contained"
            onClick={() => navigate('/auth/forgot-password')}
          >
            {t.requestNew}
          </Button>
        </Paper>
      </Container>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(t.passwordMin);
      return;
    }

    if (password !== confirmPassword) {
      setError(t.passwordMatch);
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      if (err.error_code === 'TOKEN_INVALID') {
        setError(t.invalidToken);
      } else {
        setError(err.message || 'Failed to reset password');
      }
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
          </Box>

          <Typography variant="h5" fontWeight={600} gutterBottom>
            {t.successTitle}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t.successMessage}
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/auth')}
          >
            {t.signInBtn}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t.subtitle}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label={t.passwordLabel}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label={t.confirmLabel}
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            margin="normal"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mt: 3 }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : t.resetBtn}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ResetPassword;
